import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import SanityImage from '../../media/SanityImage.client';
import sanityConfig from '../../../../sanity.config';
import {useCallback, useEffect, useState} from 'react';
import {SanityModuleCarouselSlides} from '../../../types';

type Props = {
  slides?: SanityModuleCarouselSlides[];
  isScrollable: boolean;
};

export default function CarouselClientBlock({isScrollable, slides}: Props) {
  const width =
    typeof window !== 'undefined'
      ? Math.max(window?.screen.width, window?.innerWidth)
      : 0;
  const tablet = width < 1024 && width > 640;
  const options = {
    loop: tablet,
    align: tablet ? 0.09 : 'center',
    containScroll: 'trimSnaps',
  };

  // @ts-ignore
  const [carouselRef, emblaApi] = useEmblaCarousel(options);

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, setScrollSnaps, onSelect]);

  const slideStyling =
    'max-w-[calc(90%-18px)] mr-4 min-w-[calc(90%-18px)] sm:mr-6 sm:max-w-[calc(42%-16px)] sm:min-w-[calc(42%-16px)] lg:max-w-[calc(33.33%-16px)] lg:min-w-[calc(33.33%-16px)] xl:max-w-[calc(25%-18px)] xl:min-w-[calc(25%-18px)]';

  return (
    <div>
      <div
        className={clsx('embla', isScrollable && 'overflow-hidden')}
        ref={carouselRef}
      >
        <div
          className={clsx(
            'embla__container',
            isScrollable
              ? 'flex'
              : 'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
          )}
        >
          {slides &&
            slides.map((slide) => (
              <div
                key={slide._key}
                className={clsx(
                  isScrollable && slideStyling,
                  !slide.image && 'bg-blue-light',
                  isScrollable
                    ? 'hide-scrollbar overflow-y-scroll'
                    : 'overflow-hidden',
                  'group relative rounded-md text-[#454545] first:ml-0 last:mr-0',
                )}
              >
                <div
                  className={clsx(
                    'relative aspect-square h-full min-h-[300px]',
                    slide.image ? 'flex flex-col justify-end' : '',
                  )}
                >
                  {slide.image && (
                    <SanityImage
                      alt={slide.image?.altText}
                      crop={slide.image?.crop}
                      dataset={sanityConfig.dataset}
                      hotspot={slide.image?.hotspot}
                      layout="fill"
                      objectFit="cover"
                      className={
                        slide.title &&
                        'transition duration-300 ease-in-out md:group-hover:scale-110'
                      }
                      projectId={sanityConfig.projectId}
                      sizes="25vw"
                      src={slide.image?.asset._ref}
                    />
                  )}

                  {slide.title && (
                    <div
                      className={
                        slide.image
                          ? 'overlay-bottom relative translate-y-0 p-4 text-white transition-transform duration-300 md:translate-y-full md:group-hover:translate-y-0'
                          : 'relative p-12'
                      }
                    >
                      <div className="text-md font-bold">{slide.title}</div>
                      <div className="mt-3 text-xs font-medium">
                        {slide.text}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>

      {scrollSnaps.length > 1 && (
        <div className="embla__dots my-10 flex justify-center space-x-3">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={clsx(
                'h-3 w-3 rounded-full',
                index === selectedIndex
                  ? ' embla__dot--selected bg-[#3E3E3E]'
                  : 'bg-[#B3B3B3]',
              )}
              type="button"
              onClick={() => scrollTo(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
